import { getUniqueId } from "@gettactic/helpers/src/strings/getUniqueId";
import {
  DEFAULT_DOOR_WIDTH,
  DEFAULT_WINDOW_WIDTH
} from "@gettactic/map-builder/src/components/map/defines";
import { GLOBAL_SHORTCUTS } from "@gettactic/map-builder/src/components/map/editor/shortcuts/shortcuts";
import { useShapeMetrics } from "@gettactic/map-builder/src/components/map/helpers/shapeMetrics";
import type {
  WallDoor,
  WallPathShape,
  WallShape,
  WallWindow
} from "@gettactic/map-builder/src/types";
import { raySegmentIntersection } from "@gettactic/map-builder/src/utils/geometry";
import { createWallPath } from "@gettactic/map-builder/src/utils/shapeFactory";
import {
  findWallBridge,
  type WallBridgeDirection
} from "@gettactic/map-builder/src/utils/wallBridge";
import {
  getCursorPositionOnWall,
  getCursorPositionOnWallPath
} from "@gettactic/map-builder/src/utils/wallCursorPosition";
import { getAllWallSegments } from "@gettactic/map-builder/src/utils/wallPathSegments";
import { useHotkeys } from "react-hotkeys-hook";
import { orderedShapes } from "../document/serialize";
import { useEditorStore } from "../store/EditorStoreContext";
import type { Point } from "../types";

type Opening = WallDoor | WallWindow;

const closestOpeningOnWall = (
  cursor: Point,
  wall: WallShape,
  openings: Opening[]
): Opening | null => {
  const dx = wall.x2 - wall.x;
  const dy = wall.y2 - wall.y;
  let best: Opening | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const opening of openings) {
    const distance = Math.hypot(
      cursor.x - (wall.x + opening.position * dx),
      cursor.y - (wall.y + opening.position * dy)
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      best = opening;
    }
  }
  return best;
};

const closestOpeningOnPath = (
  cursor: Point,
  path: WallPathShape,
  key: "doors" | "windows"
): { edgeIndex: number; id: string } | null => {
  const edgeCount = path.closed
    ? path.vertices.length
    : path.vertices.length - 1;
  let best: { edgeIndex: number; id: string } | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const openings = path.edges[edgeIndex]?.[key] ?? [];
    const from = path.vertices[edgeIndex];
    const to = path.vertices[(edgeIndex + 1) % path.vertices.length];
    for (const opening of openings) {
      const distance = Math.hypot(
        cursor.x - (from.x + opening.position * (to.x - from.x)),
        cursor.y - (from.y + opening.position * (to.y - from.y))
      );
      if (distance < bestDistance) {
        bestDistance = distance;
        best = { edgeIndex, id: opening.id };
      }
    }
  }
  return best;
};

/**
 * Wall keyboard shortcuts at the pointer: add/remove a door (G / Shift+G)
 * or window (W / Shift+W) on the selected wall, bridge between walls
 * (B / Shift+B), extend to the next wall (E) while a wall tool is armed.
 * Same keys and utils as the Konva editor; the pointer comes from the
 * shape-metrics context instead of the stage.
 */
export const useWallShortcuts = (): void => {
  const store = useEditorStore();
  const metrics = useShapeMetrics();

  const cursorWorld = (): Point | null => {
    if (!metrics) return null;
    const position = metrics.getPointerPosition();
    return position ? metrics.toWorld(position) : null;
  };

  const selectedWall = (): WallShape | WallPathShape | null => {
    const { selection, shapes } = store.getState();
    const shape = selection.length === 1 ? shapes[selection[0]] : undefined;
    return shape?.type === "wall" || shape?.type === "wall-path" ? shape : null;
  };

  const addOpening = (key: "doors" | "windows") => {
    const wall = selectedWall();
    const cursor = cursorWorld();
    if (!wall || !cursor) return;
    const width = key === "doors" ? DEFAULT_DOOR_WIDTH : DEFAULT_WINDOW_WIDTH;
    const id = `${key === "doors" ? "door" : "window"}-${getUniqueId()}`;
    if (wall.type === "wall") {
      const opening = {
        id,
        position: getCursorPositionOnWall(cursor, wall),
        width
      };
      store
        .getState()
        .updateShape(wall.id, { [key]: [...(wall[key] ?? []), opening] });
      return;
    }
    const hit = getCursorPositionOnWallPath(cursor, wall);
    if (!hit) return;
    const edges = [...wall.edges];
    edges[hit.edgeIndex] = {
      ...edges[hit.edgeIndex],
      [key]: [
        ...(edges[hit.edgeIndex]?.[key] ?? []),
        { id, position: hit.position, width }
      ]
    };
    store.getState().updateShape(wall.id, { edges });
  };

  const removeOpening = (key: "doors" | "windows") => {
    const wall = selectedWall();
    const cursor = cursorWorld();
    if (!wall || !cursor) return;
    if (wall.type === "wall") {
      const target = closestOpeningOnWall(cursor, wall, wall[key] ?? []);
      if (!target) return;
      store.getState().updateShape(wall.id, {
        [key]: (wall[key] ?? []).filter((opening) => opening.id !== target.id)
      });
      return;
    }
    const target = closestOpeningOnPath(cursor, wall, key);
    if (!target) return;
    const edges = [...wall.edges];
    edges[target.edgeIndex] = {
      ...edges[target.edgeIndex],
      [key]: (edges[target.edgeIndex]?.[key] ?? []).filter(
        (opening) => opening.id !== target.id
      )
    };
    store.getState().updateShape(wall.id, { edges });
  };

  const bridge = (direction: WallBridgeDirection) => {
    const cursor = cursorWorld();
    if (!cursor) return;
    const found = findWallBridge(
      cursor,
      orderedShapes(store.getState()),
      direction
    );
    if (!found) return;
    store.getState().addShapes([createWallPath([found.start, found.end])]);
  };

  const extend = () => {
    const cursor = cursorWorld();
    if (!cursor) return;
    // Extends from the last wall-path vertex placed, or from the selected
    // wall's nearest endpoint, along the snapped 45° direction to the
    // first wall hit.
    const wall = selectedWall();
    if (!wall) return;
    const anchor =
      wall.type === "wall"
        ? Math.hypot(cursor.x - wall.x2, cursor.y - wall.y2) <
          Math.hypot(cursor.x - wall.x, cursor.y - wall.y)
          ? { x: wall.x2, y: wall.y2 }
          : { x: wall.x, y: wall.y }
        : wall.vertices[wall.vertices.length - 1];
    const dx = cursor.x - anchor.x;
    const dy = cursor.y - anchor.y;
    if (Math.hypot(dx, dy) < 1) return;
    const angle =
      Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
    const direction = { x: Math.cos(angle), y: Math.sin(angle) };
    let nearest: { point: Point; distance: number } | null = null;
    for (const segment of getAllWallSegments(orderedShapes(store.getState()))) {
      const hit = raySegmentIntersection(anchor, direction, {
        start: { x: segment.x, y: segment.y },
        end: { x: segment.x2, y: segment.y2 }
      });
      if (
        hit &&
        hit.distance > 1 &&
        (!nearest || hit.distance < nearest.distance)
      )
        nearest = hit;
    }
    if (!nearest) return;
    store.getState().addShapes([createWallPath([anchor, nearest.point])]);
  };

  const options = { preventDefault: true };
  useHotkeys(GLOBAL_SHORTCUTS.addDoor, () => addOpening("doors"), options, [
    store,
    metrics
  ]);
  useHotkeys(GLOBAL_SHORTCUTS.addWindow, () => addOpening("windows"), options, [
    store,
    metrics
  ]);
  useHotkeys(
    GLOBAL_SHORTCUTS.removeDoor,
    () => removeOpening("doors"),
    options,
    [store, metrics]
  );
  useHotkeys(
    GLOBAL_SHORTCUTS.removeWindow,
    () => removeOpening("windows"),
    options,
    [store, metrics]
  );
  useHotkeys(
    GLOBAL_SHORTCUTS.wallBridgeHorizontal,
    () => bridge("horizontal"),
    options,
    [store, metrics]
  );
  useHotkeys(
    GLOBAL_SHORTCUTS.wallBridgeVertical,
    () => bridge("vertical"),
    options,
    [store, metrics]
  );
  useHotkeys(GLOBAL_SHORTCUTS.wallExtend, extend, options, [store, metrics]);
};
